import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { LogOut, User, Settings, ChevronUp } from 'lucide-react';
import { useSidebar } from '@/components/ui/sidebar';
import { UserProfileDialog } from './user-profile-dialog';

export function UserProfileSidebar() {
  const { user, profile, selectedCompany, signOut } = useAuth();
  const { open } = useSidebar();
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  if (!user || !profile) return null;

  const displayName = profile.full_name || user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario';
  const userEmail = profile.email || user.email || '';
  const avatarUrl = profile.avatar_url || user.user_metadata?.avatar_url;
  
  // Obtener iniciales para el fallback del avatar
  const initials = displayName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Versión compacta cuando el sidebar está cerrado
  if (!open) {
    return (
      <div className="p-2 border-t border-border/20">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full h-10 p-0 hover:bg-accent/50"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" className="w-56" sideOffset={8}>
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
              {selectedCompany && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedCompany.name}
                </p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setShowProfileDialog(true)}>
              <User className="mr-2 h-4 w-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Settings className="mr-2 h-4 w-4" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Modal de Perfil */}
        <UserProfileDialog 
          open={showProfileDialog} 
          onOpenChange={setShowProfileDialog} 
        />
      </div>
    );
  }

  // Versión expandida cuando el sidebar está abierto
  return (
    <div className="p-3 border-t border-border/20">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-full p-3 h-auto justify-between hover:bg-accent/50 rounded-lg"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate w-full">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground truncate w-full">
                  {selectedCompany?.name || 'Sin empresa'}
                </p>
              </div>
            </div>
            <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" className="w-56" sideOffset={8}>
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
            {selectedCompany && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedCompany.name}
              </p>
            )}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowProfileDialog(true)}>
            <User className="mr-2 h-4 w-4" />
            Perfil
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Settings className="mr-2 h-4 w-4" />
            Configuración
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar Sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Modal de Perfil */}
      <UserProfileDialog 
        open={showProfileDialog} 
        onOpenChange={setShowProfileDialog} 
      />
    </div>
  );
}